/**
 * What one cache layer did with the landing document. `not_traversed` means a
 * layer in front of it answered, so it was never asked. `unknown` means the
 * response says nothing reliable about it: no entry (CloudFront samples its
 * Server-Timing header, so a load can carry none), an unrecognised value, or an
 * outer layer whose own outcome is unknown. `prefetch` is the browser layer
 * only: the document was fetched ahead of the navigation (a prefetch or a
 * prerender), so the CDN entries describe that earlier request.
 */
export type CacheOutcome =
  | 'hit'
  | 'miss'
  | 'bypass'
  | 'dynamic'
  | 'expired'
  | 'stale'
  | 'revalidated'
  | 'prefetch'
  | 'unknown'
  | 'not_traversed'

/**
 * One cache layer per tag, outermost first: the browser, then Cloudflare, then
 * CloudFront in front of the origin. Every tenant is served the same way, so
 * every tenant reports the same tags.
 */
export type CacheOutcomes = {
  cache_browser: CacheOutcome
  cache_cloudflare: CacheOutcome
  cache_cloudfront: CacheOutcome
}

type ServerTimingEntry = { name: string; description: string }

const ALL_UNKNOWN: CacheOutcomes = {
  cache_browser: 'unknown',
  cache_cloudflare: 'unknown',
  cache_cloudfront: 'unknown',
}

// `cdn-cache` is what Cloudflare sends with RUM on, `cfCacheStatus` what
// qz.com sends today. Both carry the CF-Cache-Status value as the description.
const CLOUDFLARE_NAMES = new Set(['cdn-cache', 'cfcachestatus'])

// UPDATING serves the expired copy while the origin refreshes it, which is a
// stale hit for the reader.
const CLOUDFLARE_OUTCOMES: Record<string, CacheOutcome> = {
  HIT: 'hit',
  MISS: 'miss',
  EXPIRED: 'expired',
  STALE: 'stale',
  UPDATING: 'stale',
  BYPASS: 'bypass',
  DYNAMIC: 'dynamic',
  REVALIDATED: 'revalidated',
}

// CloudFront names the outcome rather than describing it. A refresh is a cached
// copy CloudFront confirmed with the origin before serving it.
const CLOUDFRONT_OUTCOMES: Record<string, CacheOutcome> = {
  'cdn-cache-hit': 'hit',
  'cdn-cache-miss': 'miss',
  'cdn-cache-refresh': 'revalidated',
}

const readProperty = (source: unknown, key: string): unknown =>
  typeof source === 'object' && source !== null
    ? Reflect.get(source, key)
    : undefined

const toEntries = (serverTiming: unknown): ServerTimingEntry[] => {
  if (!Array.isArray(serverTiming)) return []
  const entries: ServerTimingEntry[] = []
  for (const item of serverTiming) {
    const name = readProperty(item, 'name')
    const description = readProperty(item, 'description')
    if (typeof name !== 'string') continue
    entries.push({
      name: name.toLowerCase(),
      description: typeof description === 'string' ? description.trim() : '',
    })
  }
  return entries
}

// Several entries for one layer that disagree say nothing reliable.
const single = (found: Set<CacheOutcome>): CacheOutcome => {
  const [only] = found
  return found.size === 1 && only ? only : 'unknown'
}

const cloudflareOutcomeOf = (entries: ServerTimingEntry[]): CacheOutcome => {
  const found = new Set<CacheOutcome>()
  for (const entry of entries) {
    if (!CLOUDFLARE_NAMES.has(entry.name)) continue
    found.add(CLOUDFLARE_OUTCOMES[entry.description.toUpperCase()] ?? 'unknown')
  }
  return single(found)
}

const cloudfrontOutcomeOf = (entries: ServerTimingEntry[]): CacheOutcome => {
  const found = new Set<CacheOutcome>()
  for (const entry of entries) {
    const outcome = CLOUDFRONT_OUTCOMES[entry.name]
    if (outcome) found.add(outcome)
  }
  return single(found)
}

// A 304 carries headers only, so fewer bytes cross the network than the
// cached body holds. A full fetch carries the whole body plus headers.
const revalidatedBySize = (
  transferSize: unknown,
  encodedBodySize: unknown
): boolean =>
  typeof transferSize === 'number' &&
  typeof encodedBodySize === 'number' &&
  transferSize > 0 &&
  transferSize < encodedBodySize

/**
 * Where the browser got the document. Where the browser reports `deliveryType`
 * (Chromium), it decides: `cache` with nothing transferred is a browser cache
 * `hit`, and with bytes transferred a `revalidated` copy the server confirmed
 * with a 304. A prefetch type such as `navigational-prefetch` (or a
 * prerendered page) is `prefetch`, and empty is a full fetch over the network
 * (`miss`). Elsewhere (Safari, Firefox) only the sizes are left: nothing
 * transferred with a body is a `hit`, fewer bytes than the body is
 * `revalidated`, and the rest is a `miss`. A prefetched document also
 * transfers nothing there and can't be told apart from a browser cache hit, so
 * it counts as a `hit`.
 */
const browserOutcomeOf = (entry: object): CacheOutcome => {
  const deliveryType = readProperty(entry, 'deliveryType')
  const activationStart = readProperty(entry, 'activationStart')
  const transferSize = readProperty(entry, 'transferSize')
  const encodedBodySize = readProperty(entry, 'encodedBodySize')
  const decodedBodySize = readProperty(entry, 'decodedBodySize')
  if (deliveryType === 'cache') {
    return typeof transferSize === 'number' && transferSize > 0
      ? 'revalidated'
      : 'hit'
  }
  if (
    (typeof deliveryType === 'string' && deliveryType.includes('prefetch')) ||
    (typeof activationStart === 'number' && activationStart > 0)
  ) {
    return 'prefetch'
  }
  if (deliveryType === '') return 'miss'
  if (typeof deliveryType === 'string') return 'unknown'
  if (typeof transferSize !== 'number' || typeof decodedBodySize !== 'number') {
    return 'unknown'
  }
  if (revalidatedBySize(transferSize, encodedBodySize)) return 'revalidated'
  if (transferSize > 0) return 'miss'
  return decodedBodySize > 0 ? 'hit' : 'unknown'
}

type Traversal = 'yes' | 'no' | 'unknown'

// The browser layer decides whether the CDN saw this navigation. A browser
// cache hit never asked it. A revalidation did, but the entries are the stored
// copy's, as with a CDN revalidation. A prefetch reached it ahead of the
// navigation, so its entries are read like a network fetch's. An unknown
// browser outcome comes from the entry, not headers, so it still leaves
// Cloudflare readable.
const traversalAfterBrowser = (outcome: CacheOutcome): Traversal => {
  if (outcome === 'hit') return 'no'
  if (outcome === 'revalidated') return 'unknown'
  return 'yes'
}

/**
 * Whether the layer behind one with this outcome saw the request. A response
 * served from a cache carries the inner layers' headers as they were when it
 * was stored, so their entries are a replay. After a fresh hit the inner layers
 * were not asked at all. A stale copy is served while the origin is refreshed
 * in the background, or after the origin could not be reached, and a
 * revalidation asks the origin but keeps the cached headers, so in those cases
 * the inner layers may have seen a request but their entries don't say what
 * they did with it. An unknown outer layer may have been any of these.
 */
const nextTraversal = (outcome: CacheOutcome): Traversal => {
  if (outcome === 'hit' || outcome === 'not_traversed') return 'no'
  if (
    outcome === 'stale' ||
    outcome === 'revalidated' ||
    outcome === 'unknown'
  ) {
    return 'unknown'
  }
  return 'yes'
}

const resolveLayer = (
  traversal: Traversal,
  read: () => CacheOutcome
): CacheOutcome => {
  if (traversal === 'no') return 'not_traversed'
  if (traversal === 'unknown') return 'unknown'
  try {
    return read()
  } catch {
    return 'unknown'
  }
}

/**
 * The cache outcome per layer for the landing document, read from its
 * navigation entry. Each layer reports its own Server-Timing entry, and an
 * outer layer decides whether the next one was reached at all. Never throws:
 * anything missing or unreadable is `unknown`.
 */
export function resolveCacheOutcomes(entry: unknown): CacheOutcomes {
  try {
    if (typeof entry !== 'object' || entry === null) return ALL_UNKNOWN
    const entries = toEntries(readProperty(entry, 'serverTiming'))

    const cacheBrowser = resolveLayer('yes', () => browserOutcomeOf(entry))
    const cacheCloudflare = resolveLayer(
      traversalAfterBrowser(cacheBrowser),
      () => cloudflareOutcomeOf(entries)
    )
    const cacheCloudfront = resolveLayer(nextTraversal(cacheCloudflare), () =>
      cloudfrontOutcomeOf(entries)
    )
    return {
      cache_browser: cacheBrowser,
      cache_cloudflare: cacheCloudflare,
      cache_cloudfront: cacheCloudfront,
    }
  } catch {
    return ALL_UNKNOWN
  }
}

/**
 * The landing document's cache outcomes. A browser without navigation timing
 * reports every layer as `unknown`.
 */
export function readLandingCacheOutcomes(): CacheOutcomes {
  try {
    return resolveCacheOutcomes(performance.getEntriesByType('navigation')[0])
  } catch {
    return ALL_UNKNOWN
  }
}
